import { useState, useMemo, useEffect, useCallback } from 'react';

import { debounce } from 'lodash';

import { getCustomersList } from '../../../../../../api';
import { DEBOUNCE_DELAY, MIN_SEARCH_LENGTH, MAX_AUTOCOMPLETE_RESULTS } from '../constants';
import { EmailSuggestion } from '../types';

type UseEmailAutocompleteResult = {
  suggestions: EmailSuggestion[];
  isLoading: boolean;
  search: (query: string) => void;
  clearSuggestions: () => void;
};

export const useEmailAutocomplete = (excludeEmails: string[] = []): UseEmailAutocompleteResult => {
  const [allSuggestions, setAllSuggestions] = useState<EmailSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSuggestions = useCallback(async(query: string) => {
    setIsLoading(true);

    try {
      const response = await getCustomersList({
        search: query,
        page_size: MAX_AUTOCOMPLETE_RESULTS,
      });

      const emailSuggestions: EmailSuggestion[] = response.results
        .filter((customer) => customer.primary_email?.identifier)
        .map((customer) => ({
          email: customer.primary_email!.identifier,
          name: customer.full_name || '',
          customerId: customer.id,
        }));

      setAllSuggestions(emailSuggestions);
    } catch {
      setAllSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(
    () => debounce(fetchSuggestions, DEBOUNCE_DELAY),
    [fetchSuggestions],
  );

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const search = useCallback((query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setAllSuggestions([]);
      return;
    }
    debouncedFetch(query);
  }, [debouncedFetch]);

  const clearSuggestions = useCallback(() => {
    setAllSuggestions([]);
  }, []);

  const suggestions = useMemo(
    () => allSuggestions.filter((s) => !excludeEmails.includes(s.email)),
    [allSuggestions, excludeEmails],
  );

  return {
    suggestions,
    isLoading,
    search,
    clearSuggestions,
  };
};
